import cn from 'classnames';
import type { RefObject } from 'react';
import { memo, useMemo } from 'react';

import { Avatar, Button, Divider, Space, Typography } from '@sravni/react-design-system';
import type { ActionsType, Tab } from '@sravni/ugc/lib/specialOffers/types';

import type { EventPayload } from 'src/helpers/sendMessageToParentWindow';

import { useActionData } from '../../../../../hooks/useActionData';
import { useActiveTabType } from '../../../../../store/hooks/offers';
import type { TActionTypes } from '../../../../../types/actions';
import styles from '../../../block.module.scss';
import { useViewObserver } from '../../../hooks/useViewObserver';

interface IActionBlockProp {
  blockRef: RefObject<HTMLAnchorElement>;
  handleEvent: (event: EventPayload) => void;

  item: TActionTypes;
}

export const ActionBlock = memo<IActionBlockProp>(({ handleEvent, blockRef, item }) => {
  const activeTabType = useActiveTabType() as Tab<ActionsType>;

  const {
    actionDescription,
    actionTitle,
    actionDates,
    actionDatesTitle,
    actionDatesDescription,
    actionButton,
    logoSrc,
    clickUrl,
    advertisingCategory,
    viewAdvertisingAction,
    clickAdvertisingAction,
    advertisingLabel,
    isPrimaryButton,
    isPrimaryDate,
    isPaid,
    isShowOrganization,
  } = useActionData({ tab: activeTabType, item });

  const handleClick = () =>
    handleEvent({
      eventCategory: advertisingCategory,
      eventAction: clickAdvertisingAction,
      eventLabel: advertisingLabel,
      event: 'adEvent',
    });

  const handleView = () => {
    handleEvent({
      eventCategory: advertisingCategory,
      eventAction: viewAdvertisingAction,
      eventLabel: advertisingLabel,
      event: 'adEvent',
    });
  };

  useViewObserver({
    handleView,
    blockRef,
  });

  const secondPart = useMemo(() => {
    if (!isPaid) {
      return (
        <>
          <Typography.Text size={12} className={cn('h-mt-16', 'h-color-D30')}>
            {actionDatesTitle}
          </Typography.Text>
          <Typography.Heading level={5} as="p" className="h-mt-4 h-color-D100">
            {actionDates}
          </Typography.Heading>
        </>
      );
    }

    return (
      <>
        <Typography.Text size={12} className={cn('h-mt-16', 'h-color-D30')}>
          {actionDatesTitle}
        </Typography.Text>
        <Typography.Heading level={5} as="p" className="h-mt-4 h-color-D100">
          {actionDates}
        </Typography.Heading>
        <Typography.Text size={12} className={cn('h-mt-4', isPrimaryDate ? 'h-color-R100' : 'h-color-D30')}>
          {actionDatesDescription}
        </Typography.Text>
      </>
    );
  }, [isPaid, actionDatesTitle, actionDates, isPrimaryDate, actionDatesDescription]);

  return (
    <a
      className={cn(styles.block)}
      href={clickUrl}
      onClick={handleClick}
      target="_blank"
      ref={blockRef}
      rel="noreferrer"
    >
      <Space direction="vertical" justify="space-between" style={{ height: '100%' }}>
        <Space direction="horizontal" align="center" justify="space-between" className="h-mb-16">
          <div>
            <Typography.Text size={12} className="h-color-D30">
              {actionDescription}
            </Typography.Text>
            <Typography.Heading level={5} as="h1" className="h-mt-2 h-color-D100">
              {actionTitle}
            </Typography.Heading>
          </div>
          {isShowOrganization && <Avatar size={36} src={logoSrc} />}
        </Space>
        <div>
          <Divider />
          {secondPart}
          <Button size={44} variant={isPrimaryButton ? 'primary' : 'secondary'} block className="h-mt-16">
            {actionButton}
          </Button>
        </div>
      </Space>
    </a>
  );
});
